import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../ui";
import { showDemoNotices } from "../../../Layouts/monitoreo-ia/services/centinelaDemoMode";
import CentinelaAvatar from "./CentinelaAvatar";
import VoiceOrb from "./VoiceOrb";
import PendingActionCard from "./PendingActionCard";
import ChatHistoryPanel from "./ChatHistoryPanel";
import ChatMessageContent from "./ChatMessageContent";
import { useAiChatStore } from "../../../store/aiChatStore";
import useCentinelaConversation from "./useCentinelaConversation";
import useCentinelaVoice from "./useCentinelaVoice";
import { CHAT_CONTEXTS, CHAT_SUGGESTION_KEYS, CENTINELA_CHAT_ROUTE } from "./constants";
import { useUserStore } from "../../../store/userStore";

const PANEL_TRANSITION_MS = 280;
const COMPOSER_MAX_HEIGHT = 120;

export default function CentinelaChat() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = useUserStore((state) => state.user);

  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [inputMode, setInputMode] = useState<"text" | "voice">("text");
  const [draft, setDraft] = useState("");

  const bottomRef = useRef<HTMLDivElement>(null);
  const spokenIdRef = useRef<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;

    node.style.height = "0px";
    node.style.height = `${Math.min(node.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [draft, inputMode]);

  const context =
    CHAT_CONTEXTS.find((item) => pathname.startsWith(item.prefix)) ??
    CHAT_CONTEXTS[CHAT_CONTEXTS.length - 1];

  const {
    messages,
    welcome,
    isWelcoming,
    isLoading,
    isSimulated,
    pendingAction,
    isDeciding,
    decide,
    sendMessage,
    clear,
    threads,
    isLoadingThreads,
    isOpeningThread,
    threadsError,
    loadThreads,
    openThread,
    removeThread,
  } = useCentinelaConversation({
      context: context.id,
      operatorName: user?.first_name,
      autoStart: isMounted,
    });

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const activeThreadId = useAiChatStore((state) => state.threadId);

  const toggleHistory = useCallback(() => {
    setIsHistoryOpen((open) => {
      !open && loadThreads();
      return !open;
    });
  }, [loadThreads]);

  const startNewChat = useCallback(() => {
    setIsHistoryOpen(false);
    setDraft("");
    clear();
  }, [clear]);

  const openFromHistory = useCallback(
    (id: string) => {
      openThread(id);
      setIsHistoryOpen(false);
    },
    [openThread],
  );

  const submit = useCallback(
    (text: string) => {
      const value = text.trim();
      if (!value || isLoading) return;

      setDraft("");
      sendMessage(value);
    },
    [isLoading, sendMessage],
  );



  const voice = useCentinelaVoice(submit);

  const assistantName = t("monitoreoIa.assistantName");

  const closePanel = useCallback(() => {
    voice.release();
    setIsVisible(false);
    setInputMode("text");
    setTimeout(() => setIsMounted(false), PANEL_TRANSITION_MS);
  }, [voice]);

  const openPanel = () => {
    setIsMounted(true);
    requestAnimationFrame(() => setIsVisible(true));
  };

  useEffect(() => {
    if (!isMounted) return;

    const handleKey = (event: KeyboardEvent) => {
      event.key === "Escape" && closePanel();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [closePanel, isMounted]);

  useEffect(() => {
    isVisible && bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isVisible, messages.length, isLoading]);

  useEffect(() => {
    if (!isVisible || isLoading) return;

    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !last.content) return;
    if (spokenIdRef.current === last.id) return;

    spokenIdRef.current = last.id;
    voice.speak(last.content, last.id);
  }, [isLoading, isVisible, messages, voice]);

  const switchToVoice = () => {
    setInputMode("voice");
    voice.toggleListening();
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={openPanel}
        title={t("monitoreoIa.chat.open", { name: assistantName })}
        aria-label={t("monitoreoIa.chat.open", { name: assistantName })}
        icon={<CentinelaAvatar size="sm" />}
      />

      {isMounted && (
        <>
          <div
            role="presentation"
            onClick={closePanel}
            className={`fixed inset-0 z-70 bg-black/20 backdrop-blur-[2px] transition-opacity duration-300 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          />

          <aside
            className={`fixed right-0 top-0 z-80 flex h-full w-[420px] max-w-full flex-col overflow-hidden border-l border-line bg-app transition-transform duration-300 ease-out ${
              isVisible ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <header className="relative shrink-0 overflow-hidden border-b border-line px-5 pb-4 pt-5">
              <span className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-primary-500/20 blur-2xl" />

              <div className="relative flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <CentinelaAvatar size="md" pulse />
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-bold text-content">{assistantName}</p>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-content-secondary">
                      <i className={`${context.icon} text-[11px]`} />
                      {t(context.labelKey)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {!isSimulated && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        onClick={startNewChat}
                        title={t("monitoreoIa.chat.history.new")}
                        aria-label={t("monitoreoIa.chat.history.new")}
                        icon={<i className="ri-chat-new-line" />}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        onClick={toggleHistory}
                        title={t("monitoreoIa.chat.history.title")}
                        aria-label={t("monitoreoIa.chat.history.title")}
                        className={isHistoryOpen ? "text-primary-500" : undefined}
                        icon={<i className="ri-history-line" />}
                      />
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={voice.toggleVoiceOutput}
                    title={t(
                      voice.voiceEnabled
                        ? "monitoreoIa.chat.voice.mute"
                        : "monitoreoIa.chat.voice.unmute",
                    )}
                    aria-label={t(
                      voice.voiceEnabled
                        ? "monitoreoIa.chat.voice.mute"
                        : "monitoreoIa.chat.voice.unmute",
                    )}
                    className={voice.voiceEnabled ? "text-primary-500" : undefined}
                    icon={
                      <i
                        className={
                          voice.voiceEnabled ? "ri-volume-up-line" : "ri-volume-mute-line"
                        }
                      />
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={() => {
                      closePanel();
                      navigate(CENTINELA_CHAT_ROUTE);
                    }}
                    title={t("monitoreoIa.chat.expand")}
                    aria-label={t("monitoreoIa.chat.expand")}
                    icon={<i className="ri-fullscreen-line" />}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={closePanel}
                    title={t("monitoreoIa.chat.closePanel")}
                    aria-label={t("monitoreoIa.chat.closePanel")}
                    icon={<i className="ri-close-line text-lg" />}
                  />
                </div>
              </div>
            </header>

            <div className="relative min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {voice.orbState !== "idle" && (
                <VoiceOrb
                  state={voice.orbState}
                  transcript={voice.interimTranscript}
                  analyserNode={voice.analyserNode}
                  onClose={() => {
                    voice.closeOrb();
                    setInputMode("text");
                  }}
                />
              )}

              {isSimulated && showDemoNotices() && (
                <div className="rounded-xl border border-warning/30 bg-warning/10 p-3">
                  <p className="text-xs text-content-secondary">
                    {t("monitoreoIa.chat.simulatedNotice", { name: assistantName })}
                  </p>
                </div>
              )}

              <div className="flex animate-fadeIn gap-2.5">
                <CentinelaAvatar size="xs" className="mt-0.5" />
                <div className="min-w-0 flex-1 rounded-xl rounded-tl-none border border-line bg-subtle px-3 py-2.5 text-xs leading-relaxed text-content">
                  {isWelcoming ? (
                    <span
                      className="flex items-center gap-1 py-1"
                      aria-label={t("monitoreoIa.chat.thinking")}
                    >
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
                    </span>
                  ) : (
                    <span className="block whitespace-pre-line">
                      {welcome ?? t("monitoreoIa.chat.emptyTitle", { name: assistantName })}
                    </span>
                  )}

                  {!isWelcoming && (
                  <>
                  <div className="mt-3 border-t border-line pt-3">
                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-content-faint">
                      {t("monitoreoIa.chat.quickActions")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {CHAT_SUGGESTION_KEYS.map((key) => (
                        <button
                          key={key}
                          type="button"
                          disabled={isLoading}
                          onClick={() => submit(t(`monitoreoIa.chat.suggestions.${key}`))}
                          className="whitespace-nowrap rounded-full border border-line bg-app px-2.5 py-1.5 text-[10.5px] font-medium text-content-secondary transition-colors hover:border-primary-500/40 hover:bg-primary-500/10 hover:text-content disabled:opacity-50"
                        >
                          {t(`monitoreoIa.chat.shortcuts.${key}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
                    <button
                      type="button"
                      onClick={() => setInputMode("text")}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        inputMode === "text"
                          ? "bg-primary-500 text-on-primary"
                          : "bg-subtle text-content-secondary hover:bg-line"
                      }`}
                    >
                      <i className="ri-keyboard-line text-xs" />
                      {t("monitoreoIa.chat.modeText")}
                    </button>
                    <button
                      type="button"
                      disabled={!voice.isSupported}
                      onClick={switchToVoice}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                        inputMode === "voice"
                          ? "bg-primary-500 text-on-primary"
                          : "bg-subtle text-content-secondary hover:bg-line"
                      }`}
                    >
                      <i className="ri-mic-line text-xs" />
                      {t("monitoreoIa.chat.modeVoice")}
                    </button>
                  </div>
                  </>
                  )}
                </div>
              </div>

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2.5 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  {message.role === "assistant" ? (
                    <CentinelaAvatar size="xs" className="mt-0.5" />
                  ) : (
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-content">
                      <i className="ri-user-3-line text-[10px] text-app" />
                    </span>
                  )}

                  <div
                    className={`min-w-0 max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2.5 text-xs leading-relaxed ${
                      message.role === "user"
                        ? "rounded-tr-none bg-primary-500 text-on-primary"
                        : "rounded-tl-none border border-line bg-subtle text-content"
                    }`}
                  >
                    {message.isLoading ? (
                      <span
                        className="flex items-center gap-1 py-1"
                        aria-label={t("monitoreoIa.chat.thinking")}
                      >
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
                      </span>
                    ) : (
                      <>
                        <ChatMessageContent content={message.content} />

                        {message.role === "assistant" && (
                          <span className="mt-2 flex items-center justify-between text-[10px] text-content-faint">
                            <span className="flex items-center gap-1.5">
                              <i className="ri-check-double-line text-[9px] text-primary-500" />
                              {t("monitoreoIa.chat.aiGenerated")}
                            </span>
                            {voice.speakingId === message.id && voice.isSpeaking ? (
                              <button
                                type="button"
                                onClick={voice.stopSpeaking}
                                className="flex items-center gap-1 rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium text-primary-500 transition-colors hover:bg-primary-500/20"
                              >
                                <i className="ri-stop-circle-line text-[9px]" />
                                {t("monitoreoIa.chat.stopListening")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => voice.speak(message.content, message.id)}
                                className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] text-content-faint transition-colors hover:bg-primary-500/10 hover:text-primary-500"
                              >
                                <i className="ri-volume-up-line text-[9px]" />
                                {t("monitoreoIa.chat.listen")}
                              </button>
                            )}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {pendingAction && (
                <PendingActionCard
                  changes={pendingAction.actions}
                  isDeciding={isDeciding}
                  onApprove={() => decide("approve")}
                  onReject={() => decide("reject")}
                />
              )}

              <div ref={bottomRef} />
            </div>

            {inputMode === "text" ? (
              <form
                className="shrink-0 border-t border-line bg-app px-3 py-2.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  submit(draft);
                }}
              >
                <div className="flex items-end gap-1 rounded-2xl border border-line bg-surface py-1.5 pl-3 pr-1.5 transition-colors focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
                  <textarea
                    ref={composerRef}
                    rows={1}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" || event.shiftKey) return;
                      event.preventDefault();
                      submit(draft);
                    }}
                    placeholder={t("monitoreoIa.chat.placeholder")}
                    className="min-h-[26px] flex-1 resize-none self-center border-0 bg-transparent py-1 text-xs leading-relaxed text-content outline-none placeholder:text-content-faint"
                  />

                  <Button
                    variant="soft"
                    size="sm"
                    shape="pill"
                    iconOnly
                    type="button"
                    disabled={!voice.isSupported}
                    onClick={switchToVoice}
                    title={t("monitoreoIa.chat.modeVoice")}
                    aria-label={t("monitoreoIa.chat.modeVoice")}
                    icon={<i className="ri-mic-line text-xs" />}
                  />
                  <Button
                    size="sm"
                    shape="pill"
                    type="submit"
                    iconOnly
                    loading={isLoading}
                    disabled={draft.trim().length === 0}
                    aria-label={t("monitoreoIa.chat.send")}
                    icon={<i className="ri-send-plane-fill text-xs" />}
                  />
                </div>

                {draft.trim().length > 0 && (
                  <p className="mt-1.5 px-1 text-[10px] text-content-faint">
                    {t("monitoreoIa.chat.composerHint")}
                  </p>
                )}
              </form>
            ) : (
              <div className="flex shrink-0 items-center gap-2 border-t border-line bg-app px-3 py-2.5">
                <Button
                  variant="secondary"
                  size="sm"
                  shape="pill"
                  iconOnly
                  onClick={() => {
                    voice.closeOrb();
                    setInputMode("text");
                  }}
                  title={t("monitoreoIa.chat.modeText")}
                  aria-label={t("monitoreoIa.chat.modeText")}
                  icon={<i className="ri-keyboard-line text-sm" />}
                />
                <button
                  type="button"
                  onClick={voice.toggleListening}
                  className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-full text-xs font-medium shadow-sm transition-colors ${
                    voice.isListening
                      ? "bg-error text-white hover:brightness-95"
                      : "bg-primary-500 text-on-primary hover:bg-primary-400"
                  }`}
                >
                  {voice.isListening ? (
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                    </span>
                  ) : (
                    <i className="ri-mic-fill text-sm" />
                  )}
                  {t(
                    voice.isListening
                      ? "monitoreoIa.chat.voice.stopHint"
                      : "monitoreoIa.chat.voice.startHint",
                    { name: assistantName },
                  )}
                </button>
              </div>
            )}

            <p className="shrink-0 border-t border-line bg-subtle/50 px-5 py-2.5 text-center text-[10px] text-content-faint">
              {t("monitoreoIa.chat.footer", { name: assistantName })}
            </p>

            {isHistoryOpen && (
              <ChatHistoryPanel
                threads={threads}
                activeThreadId={activeThreadId}
                isLoading={isLoadingThreads}
                isOpening={isOpeningThread}
                error={threadsError}
                onOpen={openFromHistory}
                onRemove={removeThread}
                onClose={() => setIsHistoryOpen(false)}
              />
            )}
          </aside>
        </>
      )}
    </>
  );
}
